import { useEffect, useRef, useState } from 'react'
import { inicioDelReloj } from './inactividad'
import { SESSION_START_KEY } from './session'

const IDLE_MS   = 2 * 60 * 60 * 1000  // 2 h de inactividad total
const WARNING_MS = 2 * 60 * 1000       // 2 min de aviso antes del logout
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'pointermove'] as const
const STORAGE_KEY = 'pgh_last_activity'

interface UseIdleTimeoutOptions {
  onTimeout: () => void
  enabled: boolean
}

interface UseIdleTimeoutResult {
  warningVisible: boolean
  secondsRemaining: number
  stayLoggedIn: () => void
}

export function useIdleTimeout({ onTimeout, enabled }: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const [warningVisible, setWarningVisible] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(Math.floor(WARNING_MS / 1000))
  const warnTimerRef   = useRef<number | null>(null)
  const logoutTimerRef = useRef<number | null>(null)
  const tickTimerRef   = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled) return

    const clearTimers = () => {
      if (warnTimerRef.current   !== null) { clearTimeout(warnTimerRef.current);   warnTimerRef.current   = null }
      if (logoutTimerRef.current !== null) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null }
      if (tickTimerRef.current   !== null) { clearInterval(tickTimerRef.current);  tickTimerRef.current   = null }
    }

    const scheduleFrom = (lastActivity: number) => {
      clearTimers()
      const elapsed = Date.now() - lastActivity
      const untilWarning = IDLE_MS - WARNING_MS - elapsed
      const untilLogout  = IDLE_MS - elapsed

      if (untilLogout <= 0) {
        setWarningVisible(false)
        onTimeout()
        return
      }

      if (untilWarning <= 0) {
        setWarningVisible(true)
        setSecondsRemaining(Math.max(1, Math.ceil(untilLogout / 1000)))
        tickTimerRef.current = window.setInterval(() => {
          const remaining = IDLE_MS - (Date.now() - lastActivity)
          if (remaining <= 0) {
            setWarningVisible(false)
            onTimeout()
            return
          }
          setSecondsRemaining(Math.max(1, Math.ceil(remaining / 1000)))
        }, 1000)
        logoutTimerRef.current = window.setTimeout(() => {
          setWarningVisible(false)
          onTimeout()
        }, untilLogout)
        return
      }

      setWarningVisible(false)
      warnTimerRef.current = window.setTimeout(() => scheduleFrom(lastActivity), untilWarning)
    }

    const markActivity = () => {
      if (warningVisible) return  // durante el aviso los eventos no cuentan como actividad
      const now = Date.now()
      localStorage.setItem(STORAGE_KEY, String(now))
      scheduleFrom(now)
    }

    // Inicial: usar timestamp previo si existe (mantiene el reloj entre pestañas),
    // salvo que sea anterior al login: ese es de una sesión que ya terminó.
    const stored = Number(localStorage.getItem(STORAGE_KEY) ?? '0')
    const initial = inicioDelReloj(
      stored, Number(sessionStorage.getItem(SESSION_START_KEY) ?? '0'), Date.now())
    if (initial !== stored) localStorage.setItem(STORAGE_KEY, String(initial))
    scheduleFrom(initial)

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActivity, { passive: true }))

    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return
      const remote = Number(e.newValue)
      if (Number.isFinite(remote) && remote > 0) scheduleFrom(remote)
    }
    window.addEventListener('storage', onStorage)

    return () => {
      clearTimers()
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActivity))
      window.removeEventListener('storage', onStorage)
    }
  }, [enabled, onTimeout, warningVisible])

  const stayLoggedIn = () => {
    const now = Date.now()
    localStorage.setItem(STORAGE_KEY, String(now))
    setWarningVisible(false)
    // el efecto detectará el cambio a warningVisible=false y `markActivity` reprogramará
    // en el siguiente evento, pero además reprogramamos ya con el timestamp nuevo:
    window.dispatchEvent(new Event('pointermove'))
  }

  return { warningVisible, secondsRemaining, stayLoggedIn }
}

export function clearIdleState() {
  localStorage.removeItem(STORAGE_KEY)
}
